/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import { Country } from 'react-phone-number-input';
import { Input, PhoneNumberInput, Select, TextBox } from '../../index';
import useValidator from '../../../hooks/useValidator';
import { MiscFormActions } from '../../../pages/Misc/Misc';
import countries from '../../../utils/countries';
import { Customer } from '../../../pages/Customers/Customers';

export type CustomerFormDataType = {
  customerId?: string;
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword?: string;
  address: string;
  otherAddress: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  notes: string;
  role: string;
};

type CustomerProps = {
  customer: Customer | undefined;
};

const CustomerForm = React.forwardRef<MiscFormActions, CustomerProps>(
  ({ customer }, ref) => {
    const [account, setAccount] = React.useState({
      name: customer?.name || '',
      email: customer?.email || '',
      phoneNumber: customer?.phoneNumber || '',
      password: '',
      confirmPassword: '',
      address: customer?.address || '',
      otherAddress: customer?.otherAddress || '',
      city: customer?.city || '',
      state: customer?.state || '',
      postalCode: customer?.postalCode || '',
      countryCode: customer?.countryCode || 'GH',
      notes: customer?.notes || '',
      role: 'Customer',
    });
    const [errors, setErrors] = React.useState({
      name: false,
      email: false,
      phoneNumber: false,
      password: false,
      confirmPassword: false,
    });
    const [validator, showValidationMessage] = useValidator({});

    React.useImperativeHandle(ref, () => {
      return {
        getFormData: customerFormData,
      };
    });

    const handleAccountInputChange = (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = event.target;

      setAccount(fndAccount => ({
        ...fndAccount,
        [name]: value,
      }));
    };

    const customerFormData = (): CustomerFormDataType | null => {
      if (!validator.allValid()) {
        setErrors(errs => ({
          ...errs,
          name: !validator.fieldValid('name'),
          email: !validator.fieldValid('email'),
          phoneNumber: !validator.fieldValid('phoneNumber'),
          password: !validator.fieldValid('password'),
          confirmPassword: !validator.fieldValid('confirmPassword'),
        }));
        showValidationMessage(true);
        return null;
      }

      const fndAccount = {
        ...account,
        ...(customer &&
          customer?.customerId && { customerId: customer.customerId }),
      };

      return fndAccount;
    };

    return (
      <div className="h-full bg-white">
        <form className="w-full h-full flex flex-row space-x-10 items-start">
          <div className="rounded-md w-[50%] border border-[rgba(84,174,255,0.4)]">
            <div
              className="h-fit py-2 px-5 bg-[#ddf4ff] rounded-tl-md rounded-tr-md border-b 
      border-[rgba(84,174,255,0.4)]"
            >
              <h2 className="text-xbase font-semibold">Details</h2>
            </div>
            <div className="pt-3 pb-5 px-5">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="text-xbase block mb-1">
                    Name{' '}
                    <span className="text-xs font-light text-pos-red">*</span>
                  </label>
                  <Input
                    name="name"
                    id="name"
                    value={account.name}
                    onChange={handleAccountInputChange}
                    onBlur={() => {
                      setErrors(errs => ({
                        ...errs,
                        name: !validator.check(account.name, 'required|min:2'),
                      }));
                    }}
                    error={errors.name}
                  />
                  {validator.message('name', account.name, 'required|min:2', {
                    messages: {
                      required: 'Full name is required.',
                    },
                    className:
                      'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                  })}
                </div>

                <div className="flex flex-row gap-5">
                  <div className="flex-1 w-full">
                    <label htmlFor="email" className="text-xbase block mb-1">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      value={account.email}
                      type="email"
                      autoComplete="username"
                      onChange={handleAccountInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          email: !validator.check(account.email, 'email'),
                        }));
                      }}
                      error={errors.email}
                    />
                    {validator.message('email', account.email, 'email', {
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    })}
                  </div>

                  <div className="w-[40%]">
                    <label
                      htmlFor="phoneNumber"
                      className="text-xbase block mb-1"
                    >
                      Phone
                    </label>
                    <PhoneNumberInput
                      id="phoneNumber"
                      name="phoneNumber"
                      defaultCountry={
                        account.countryCode
                          ? (account.countryCode as Country)
                          : 'GH'
                      }
                      value={account.phoneNumber}
                      onChange={value =>
                        setAccount(fndAccount => ({
                          ...fndAccount,
                          phoneNumber: value,
                        }))
                      }
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          phoneNumber: !validator.check(
                            account.phoneNumber,
                            'phone',
                          ),
                        }));
                      }}
                      error={errors.phoneNumber}
                    />
                    {validator.message(
                      'phoneNumber',
                      account.phoneNumber,
                      'phone',
                      {
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                </div>

                <div className="flex flex-row gap-5">
                  <div className="w-full">
                    <label htmlFor="password" className="text-xbase block mb-1">
                      Password
                    </label>
                    <Input
                      id="password"
                      name="password"
                      value={account.password}
                      type="password"
                      onChange={handleAccountInputChange}
                      autoComplete="new-password"
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          password: !validator.check(account.password, 'min:8'),
                        }));
                      }}
                      error={errors.password}
                    />
                    {validator.message('password', account.password, 'min:8', {
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    })}
                  </div>

                  <div className="w-full">
                    <label
                      htmlFor="confirmPassword"
                      className="text-xbase block mb-1"
                    >
                      Confirm Password
                    </label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      value={account.confirmPassword}
                      type="password"
                      onChange={handleAccountInputChange}
                      autoComplete="new-password"
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          confirmPassword: !validator.check(
                            account.confirmPassword,
                            'min:8',
                          ),
                        }));
                      }}
                      error={errors.confirmPassword}
                    />
                    {validator.message(
                      'confirmPassword',
                      account.confirmPassword,
                      'min:8',
                      {
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="border-b border-[#d0d7de]">
                  <h2 className="text-xbase font-semibold">
                    Notes & Preferences
                  </h2>
                </div>

                <div className="flex flex-row mt-6">
                  <TextBox
                    id="notes"
                    name="notes"
                    value={account.notes}
                    onChange={handleAccountInputChange}
                  />
                </div>

                {/* <div className="flex flex-row  mt-4 space-x-10">
              <div className="">
                <label htmlFor="chargeTax" className="text-xbase block mb-1">
                  Taxable
                </label>
                <ToggleInput
                  id="chargeTax"
                  name="chargeTax"
                  value="chargeTax"
                  checked={chargeTax}
                  onChange={event => setChargeTax(event.target.checked)}
                  className="align-middle cursor-pointer"
                />
              </div>

              <div className="">
                <label htmlFor="chargeTax" className="text-xbase block mb-1">
                  Email Marketing
                </label>
                <ToggleInput
                  id="chargeTax"
                  name="chargeTax"
                  value="chargeTax"
                  checked={chargeTax}
                  onChange={event => setChargeTax(event.target.checked)}
                  className="align-middle cursor-pointer"
                />
              </div>
            </div> */}

                <div className="flex flex-row  mt-4">
                  <h2 className="text-xbase font-semibold">
                    Last Login:{' '}
                    <span className="font-light text-pos-gray">
                      No Activity Yet
                    </span>
                  </h2>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-md w-[50%] border border-[rgba(84,174,255,0.4)]">
            <div
              className="h-fit py-2 px-5 bg-[#ddf4ff] rounded-tl-md rounded-tr-md border-b 
        border-[rgba(84,174,255,0.4)]"
            >
              <h2 className="text-xbase font-semibold">Address</h2>
            </div>
            <div className="pt-3 pb-5 px-5">
              <div className="space-y-4">
                <div>
                  <label htmlFor="address" className="text-xbase block mb-1">
                    Address
                  </label>
                  <Input
                    id="address"
                    name="address"
                    value={account.address}
                    onChange={handleAccountInputChange}
                  />
                </div>

                <div>
                  <label
                    htmlFor="otherAddress"
                    className="text-xbase block mb-1"
                  >
                    Other Address
                  </label>
                  <Input
                    id="otherAddress"
                    name="otherAddress"
                    value={account.otherAddress}
                    onChange={handleAccountInputChange}
                  />
                </div>

                <div className="flex flex-row gap-5">
                  <div className="flex-1">
                    <label
                      htmlFor="countryCode"
                      className="text-xbase block mb-1"
                    >
                      Country
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: '',
                      }}
                      value={account.countryCode}
                      options={countries.map(fndCountry => ({
                        value: fndCountry.iso,
                        label: fndCountry.name,
                      }))}
                      name="countryCode"
                      id="countryCode"
                      onChange={handleAccountInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="text-xbase block mb-1">
                      City
                    </label>
                    <Input
                      name="city"
                      id="city"
                      value={account.city}
                      onChange={handleAccountInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="text-xbase block mb-1">
                      State / Region
                    </label>
                    <Input
                      id="state"
                      name="state"
                      value={account.state}
                      onChange={handleAccountInputChange}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="postalCode" className="text-xbase block mb-1">
                    Postal code
                  </label>
                  <Input
                    id="postalCode"
                    name="postalCode"
                    value={account.postalCode}
                    onChange={handleAccountInputChange}
                    className="w-[250px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  },
);

export default CustomerForm;
